import type { Platform } from '../contexts/PostContext';

/**
 * Apply platform-specific text formatting
 */
export function applyFormatting(
  text: string,
  format: 'bold' | 'italic' | 'strikethrough' | 'monospace',
  platform: Platform
): string {
  if (platform === 'whatsapp') {
    return applyWhatsAppFormatting(text, format);
  } else if (platform === 'linkedin') {
    return applyLinkedInFormatting(text, format);
  } else {
    // Twitter/X doesn't support formatting
    return text;
  }
}

/**
 * WhatsApp formatting:
 * - Bold: *text* (toggle)
 * - Italic: _text_ (toggle)
 * - Strikethrough: ~text~
 * - Monospace: ```text```
 */
function applyWhatsAppFormatting(
  text: string,
  format: 'bold' | 'italic' | 'strikethrough' | 'monospace'
): string {
  switch (format) {
    case 'bold':
      return text.startsWith('*') && text.endsWith('*') && text.length > 2
        ? text.slice(1, -1)   // toggle off
        : `*${text}*`;        // toggle on
    case 'italic':
      return text.startsWith('_') && text.endsWith('_') && text.length > 2
        ? text.slice(1, -1)
        : `_${text}_`;
    case 'strikethrough':
      return text.startsWith('~') && text.endsWith('~') && text.length > 2
        ? text.slice(1, -1)
        : `~${text}~`;
    case 'monospace':
      return text.startsWith('```') && text.endsWith('```') && text.length > 6
        ? text.slice(3, -3)
        : `\`\`\`${text}\`\`\``;
    default:
      return text;
  }
}

/**
 * LinkedIn formatting — store as markdown markers in the editor (plain text).
 * The preview and clipboard both render these markers into Unicode.
 */
function applyLinkedInFormatting(
  text: string,
  format: 'bold' | 'italic' | 'strikethrough' | 'monospace'
): string {
  if (format === 'bold') {
    return text.startsWith('**') && text.endsWith('**') && text.length > 4
      ? text.slice(2, -2)   // toggle off
      : `**${text}**`;      // toggle on
  }
  if (format === 'italic') {
    return text.startsWith('_') && text.endsWith('_') && text.length > 2
      ? text.slice(1, -1)
      : `_${text}_`;
  }
  return text;
}

/**
 * Render content for a platform — converts markdown markers to platform-specific
 * encoding. Used by Copy/Share so the editor stays plain text.
 *
 * For LinkedIn: Latin chars → Unicode bold (LinkedIn native bold).
 *               Hebrew/Arabic/other chars: markers stripped (no Unicode bold exists).
 * For WhatsApp: *bold* / _italic_ markers are the native format — returned as-is.
 */
export function renderForPlatform(text: string, platform: Platform): string {
  if (platform === 'linkedin') {
    return text
      .replace(/\*\*(.+?)\*\*/gs, (_, t) => convertToBoldUnicode(t))
      .replace(/_(.+?)_/gs, (_, t) => convertToItalicUnicode(t));
  }
  return text;
}

/**
 * Convert text to Unicode bold characters
 */
function convertToBoldUnicode(text: string): string {
  const boldMap: Record<string, string> = {
    'A': '𝗔', 'B': '𝗕', 'C': '𝗖', 'D': '𝗗', 'E': '𝗘', 'F': '𝗙', 'G': '𝗚', 'H': '𝗛',
    'I': '𝗜', 'J': '𝗝', 'K': '𝗞', 'L': '𝗟', 'M': '𝗠', 'N': '𝗡', 'O': '𝗢', 'P': '𝗣',
    'Q': '𝗤', 'R': '𝗥', 'S': '𝗦', 'T': '𝗧', 'U': '𝗨', 'V': '𝗩', 'W': '𝗪', 'X': '𝗫',
    'Y': '𝗬', 'Z': '𝗭',
    'a': '𝗮', 'b': '𝗯', 'c': '𝗰', 'd': '𝗱', 'e': '𝗲', 'f': '𝗳', 'g': '𝗴', 'h': '𝗵',
    'i': '𝗶', 'j': '𝗷', 'k': '𝗸', 'l': '𝗹', 'm': '𝗺', 'n': '𝗻', 'o': '𝗼', 'p': '𝗽',
    'q': '𝗾', 'r': '𝗿', 's': '𝘀', 't': '𝘁', 'u': '𝘂', 'v': '𝘃', 'w': '𝘄', 'x': '𝘅',
    'y': '𝘆', 'z': '𝘇',
    '0': '𝟬', '1': '𝟭', '2': '𝟮', '3': '𝟯', '4': '𝟰', '5': '𝟱', '6': '𝟲', '7': '𝟳',
    '8': '𝟴', '9': '𝟵',
  };

  return text
    .split('')
    .map((char) => boldMap[char] || char)
    .join('');
}

/**
 * Convert text to Unicode italic characters
 */
function convertToItalicUnicode(text: string): string {
  const italicMap: Record<string, string> = {
    'A': '𝘈', 'B': '𝘉', 'C': '𝘊', 'D': '𝘋', 'E': '𝘌', 'F': '𝘍', 'G': '𝘎', 'H': '𝘏',
    'I': '𝘐', 'J': '𝘑', 'K': '𝘒', 'L': '𝘓', 'M': '𝘔', 'N': '𝘕', 'O': '𝘖', 'P': '𝘗',
    'Q': '𝘘', 'R': '𝘙', 'S': '𝘚', 'T': '𝘛', 'U': '𝘜', 'V': '𝘝', 'W': '𝘞', 'X': '𝘟',
    'Y': '𝘠', 'Z': '𝘡',
    'a': '𝘢', 'b': '𝘣', 'c': '𝘤', 'd': '𝘥', 'e': '𝘦', 'f': '𝘧', 'g': '𝘨', 'h': '𝘩',
    'i': '𝘪', 'j': '𝘫', 'k': '𝘬', 'l': '𝘭', 'm': '𝘮', 'n': '𝘯', 'o': '𝘰', 'p': '𝘱',
    'q': '𝘲', 'r': '𝘳', 's': '𝘴', 't': '𝘵', 'u': '𝘶', 'v': '𝘷', 'w': '𝘸', 'x': '𝘹',
    'y': '𝘺', 'z': '𝘻',
  };

  return text
    .split('')
    .map((char) => italicMap[char] || char)
    .join('');
}

/**
 * Get character limit for platform
 */
export function getCharacterLimit(platform: Platform): number {
  switch (platform) {
    case 'twitter':
      return 280;
    case 'linkedin':
      return 3000;
    case 'whatsapp':
      return 65536;
    default:
      return 3000;
  }
}

/**
 * Check if text exceeds platform limit
 */
export function isOverLimit(text: string, platform: Platform): boolean {
  return text.length > getCharacterLimit(platform);
}
